import { useCallback, useEffect, useState } from 'react';
import { Receipt } from 'lucide-react';
import { NumberField } from '@renderer/components/ui/NumberField';
import {
  CLAVE_IMPUESTO_INCLUIDO,
  CLAVE_IMPUESTO_PORCENTAJE,
  impuestoEstaIncluido,
  LEYENDA_IMPUESTO_INCLUIDO,
} from '@shared/impuesto';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 73 — Cómo maneja el negocio el impuesto.
 *
 * ## Por qué esta pantalla no existía, y lo que costó
 *
 * El impuesto **sólo se preguntaba en el asistente de instalación**, y después no había forma de
 * cambiarlo. Quedó en 16% y nadie pudo corregirlo: el sistema le estaba **sumando 16% encima** a
 * precios que la dueña había capturado con el impuesto ya adentro. Su Espada Individual de $185 se
 * cobraba en $214.60.
 *
 * Un dato que se pregunta una sola vez, en la instalación, y que después no se puede tocar, es un
 * dato que va a quedar mal. Esta pantalla es el arreglo de fondo.
 *
 * ## La forma de la decisión
 *
 * La propuso José y es la correcta: no se cablea "el IVA va incluido" porque la dueña lo pidió — se
 * configura, porque habrá negocios que necesiten desglosarlo, y quien factura lo necesita por ley.
 */

type Modo = 'incluido' | 'calculado';

export function ImpuestosForm() {
  const [modo, setModo] = useState<Modo>('incluido');
  const [porcentaje, setPorcentaje] = useState(16);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ tipo: 'ok' | 'error'; texto: string } | null>(null);

  const cargar = useCallback(async () => {
    const [inc, pct] = await Promise.all([
      window.api.config.obtener(CLAVE_IMPUESTO_INCLUIDO),
      window.api.config.obtener(CLAVE_IMPUESTO_PORCENTAJE),
    ]);
    setModo(impuestoEstaIncluido(inc.ok ? inc.data : null) ? 'incluido' : 'calculado');
    const n = Number(pct.ok ? pct.data : null);
    if (Number.isFinite(n) && n > 0) setPorcentaje(n);
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    const r1 = await window.api.config.actualizar(
      CLAVE_IMPUESTO_INCLUIDO,
      modo === 'incluido' ? 'true' : 'false',
    );
    const r2 = await window.api.config.actualizar(CLAVE_IMPUESTO_PORCENTAJE, String(porcentaje));
    setGuardando(false);
    if (!r1.ok || !r2.ok) {
      setMensaje({ tipo: 'error', texto: traducirErrorBackend((!r1.ok && r1.error) || (!r2.ok && r2.error) || '') });
      return;
    }
    setMensaje({
      tipo: 'ok',
      texto:
        modo === 'incluido'
          ? 'Listo. Los precios se cobran tal cual y el ticket lo dirá.'
          : `Listo. A partir de ahora se suma ${porcentaje}% sobre el subtotal.`,
    });
  };

  if (cargando) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="card max-w-2xl space-y-4">
      <div className="flex items-start gap-2">
        <Receipt className="mt-0.5 h-5 w-5 shrink-0 text-slate-500" />
        <div>
          <h2 className="font-semibold">Impuestos</h2>
          <p className="text-sm text-slate-600">
            Cómo se cobra el impuesto sobre los precios que capturaste en el menú.
          </p>
        </div>
      </div>

      <fieldset className="space-y-2">
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
          <input
            type="radio"
            className="mt-1 h-5 w-5"
            checked={modo === 'incluido'}
            onChange={() => setModo('incluido')}
          />
          <span className="text-sm">
            <span className="font-medium">Los precios ya incluyen impuestos</span>
            <span className="mt-0.5 block text-slate-600">
              El precio del menú es lo que paga el cliente. En el ticket, en lugar del renglón de
              impuesto, sale: <em>“{LEYENDA_IMPUESTO_INCLUIDO}”</em>.
            </span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Es lo normal en un restaurante que no factura.
            </span>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50">
          <input
            type="radio"
            className="mt-1 h-5 w-5"
            checked={modo === 'calculado'}
            onChange={() => setModo('calculado')}
          />
          <span className="text-sm">
            <span className="font-medium">Calcular el impuesto y sumarlo</span>
            <span className="mt-0.5 block text-slate-600">
              Los precios del menú son <strong>sin</strong> impuesto: se agrega al cobrar y se
              desglosa en el ticket.
            </span>
          </span>
        </label>
      </fieldset>

      {modo === 'calculado' && (
        <div className="max-w-[180px]">
          <label className="label" htmlFor="imp-pct">
            Porcentaje a sumar
          </label>
          <div className="flex items-center gap-2">
            <NumberField
              id="imp-pct"
              className="input text-right"
              min={0}
              decimals={2}
              value={porcentaje}
              onChange={setPorcentaje}
            />
            <span className="text-sm text-slate-600">%</span>
          </div>
        </div>
      )}

      {/*
        El aviso que evita el susto: cambiar esto NO reescribe lo ya cobrado. Cada pedido guardó su
        impuesto en su propia fila, y eso es lo único honesto con un histórico — si al cambiar la
        opción los tickets viejos cambiaran de total, ningún corte cuadraría con su PDF.
      */}
      <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
        El cambio aplica a las ventas <strong>nuevas</strong>. Los pedidos ya cobrados conservan el
        impuesto con el que se cobraron: sus tickets y sus cortes siguen cuadrando.
      </p>

      {mensaje && (
        <div
          role="status"
          className={`rounded-lg px-3 py-2 text-sm ${
            mensaje.tipo === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'
          }`}
        >
          {mensaje.texto}
        </div>
      )}

      <div>
        <button
          onClick={() => void guardar()}
          className="btn-primary min-h-[44px]"
          disabled={guardando}
        >
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}
